import React, { type JSX } from 'react';

import { Header } from '@fiji/common/src/types';
import { CustomTable, CustomTransComponent, GroupPath, Hierarchy } from '../../../components';
import { EventListingItem } from '../../../components/EventListingItem/EventListingItem';
import { useTypedSelector } from '@fiji/common/src/app/store';
import { useNavigate, useParams } from 'react-router-dom';
import ChevronRight from '@mui/icons-material/ChevronRight';
import Search from '@mui/icons-material/Search';
import Update from '@mui/icons-material/Update';
import { useGetPredictionFiltersQuery } from '@fiji/common/src/features/prediction/predictionApi';
import { Chip, IconButton, Paper, Stack, TextField } from '@mui/material';
import { selectedOrg } from '@fiji/common/src/features/orgManagement/orgSlice';
import { useGetAllGroupsMutation } from '@fiji/common/src/features/group/groupApi';
import { GroupHierarchySkeleton } from 'pages/GroupHierarchy/GroupHierarchySkeleton';
import { useDebounce, useIsMount } from '@fiji/common/src/hooks';
import { useFilteredValues } from 'hooks/useFilteredValues';
import { toFirstLetterUpperCase } from 'utils/helpers';
import { getUpcomingStatusTagProps } from 'utils/upcomingStatusTag';
import { ListItemTag } from '@brightlayer-ui/react-components';
import { useTranslation } from 'react-i18next';
import { useRBAC } from 'hooks';
import { selectCurrentPermission } from '@fiji/common/src/features/profile/profileSlice';

type TablePayload = {
    page: number;
    size: number;
    searchKey?: string;
    filters: Record<string, any>;
    sort?: Record<string, any>;
};

type UpcomingApiResponse = {
    data?: {
        records?: any[];
        total?: number;
    };
};

const areStringArraysEqual = (first: string[], second: string[]): boolean => {
    if (first.length !== second.length) {
        return false;
    }

    return first.every((value, index) => value === second[index]);
};

const getGroupHierarchyRecords = (groupsHierarchy: any): any[] | undefined => {
    if (groupsHierarchy && groupsHierarchy.data && Array.isArray(groupsHierarchy.data.records)) {
        return groupsHierarchy.data.records;
    }

    return undefined;
};

const resolveUpcomingRecordId = (record: any, index: number): string => {
    const safeRecord = record && typeof record === 'object' ? record : {};
    const resolvedId = safeRecord.id || safeRecord.predictionId || safeRecord.upcomingId;
    if (resolvedId) {
        return String(resolvedId);
    }

    const fallbackDeviceId = safeRecord.deviceId || 'device';
    const fallbackType = safeRecord.type || 'type';
    const fallbackWithin = safeRecord.within || 'within';
    return `fallback-${fallbackDeviceId}-${fallbackType}-${fallbackWithin}-${index}`;
};

const resolveUpcomingsData = (upcomingsApiData: UpcomingApiResponse | undefined, fallbackDeviceId?: string): any => {
    const payloadData = upcomingsApiData && upcomingsApiData.data ? upcomingsApiData.data : {};
    const predictionRecords = Array.isArray(payloadData.records) ? payloadData.records : [];

    const records = predictionRecords.map((record: any, index: number) => ({
        ...record,
        id: resolveUpcomingRecordId(record, index),
        deviceId: (record && record.deviceId) || fallbackDeviceId || '',
    }));

    return {
        records,
        total: typeof payloadData.total === 'number' ? payloadData.total : records.length,
    };
};

const getLeftBorderStyle = (indicator?: string): Record<string, string | number> | undefined => {
    if (typeof indicator !== 'string' || !indicator.trim()) {
        return undefined;
    }

    return {
        content: '" "',
        position: 'absolute',
        top: '0px',
        bottom: '-2px',
        left: '0px',
        width: '6px',
        zIndex: 100,
        backgroundColor: indicator.trim(),
    };
};

const getStatusCell = (item: any): JSX.Element => (
    <EventListingItem icon={item?.icon} within={item?.within} column="status" />
);

const getDaysCell = (data: any): JSX.Element => <EventListingItem within={data?.within} column="within" />;

const getGroupCell = (data: any): JSX.Element => <GroupPath isFirstBold={true} pathName={data?.groupPath ?? []} />;

const getTypeCell = (data: any): JSX.Element => (
    <EventListingItem type={data?.type} within={data?.within} column="type" />
);

const getWithinFilter = (withinValues: unknown): Record<string, unknown> => {
    if (!Array.isArray(withinValues) || withinValues.length === 0) {
        return {};
    }

    return { within: withinValues[0] };
};

export const UpcomingEvents = (): JSX.Element => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { deviceId } = useParams();
    const isMount = useIsMount();
    const tableRef: any = React.useRef(undefined);

    const currentRealmName = useTypedSelector((state) => state.common.selectedRealm);
    const currentOrg = useTypedSelector(selectedOrg);
    const permissions = useTypedSelector(selectCurrentPermission);
    const { hasPermission } = useRBAC(permissions);
    const canReadFilteredPredictions = hasPermission('prediction-notifications_Read-filtered-predictions');
    const canViewPrediction = hasPermission('prediction-notifications_view-prediction');
    const {
        id: currentOrgId,
        defaultGroupAccess,
        name: currentOrgName,
        groupCount: currentOrgGroupCount,
        deviceCount: currentOrgDeviceCount,
    } = currentOrg ?? {};
    const [searchKey, setSearchKey] = React.useState<string>('');

    const [, debouncedSearchKey] = useDebounce(undefined, 600, searchKey);

    const [payload, setPayload] = React.useState<TablePayload>({
        page: 0,
        size: 10,
        filters: {},
    });

    const [selectedGroups, setSelectedGroups] = React.useState<string[]>([]);
    const [groupsData, setGroupsData] = React.useState<any>();
    const [hierarchyMappingData, setHierarchyMappingData] = React.useState({});
    const [loadingHierarchyNode, setLoadingHierarchyNode] = React.useState([]);
    const [getAllGroups, { data: groupsHierarchy }]: any = useGetAllGroupsMutation();

    React.useEffect(() => {
        if (!isMount) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                searchKey: debouncedSearchKey,
            }));
        }
    }, [debouncedSearchKey]);

    React.useEffect(() => {
        if (!isMount) {
            setPayload((prev: any) => {
                const existingGroupIds = Array.isArray(prev?.filters?.groupId) ? prev.filters.groupId : [];
                const hasSameSelection = areStringArraysEqual(existingGroupIds, selectedGroups);

                if (hasSameSelection) {
                    return prev;
                }

                const nextFilters = { ...prev.filters };
                if (selectedGroups.length > 0) {
                    nextFilters.groupId = selectedGroups;
                } else {
                    delete nextFilters.groupId;
                }

                return {
                    ...prev,
                    page: 0,
                    filters: nextFilters,
                };
            });
        }
    }, [isMount, selectedGroups]);

    React.useEffect(() => {
        if (!groupsData) {
            setGroupsData(getGroupHierarchyRecords(groupsHierarchy));
        }
    }, [groupsHierarchy, groupsData]);

    React.useEffect(() => {
        if (currentOrgId) {
            getAllGroups({});
        }
    }, [currentOrgId, getAllGroups]);

    const handleFetchHierarchy = async (parentData: any): Promise<void> => {
        const parentId = parentData?.id;
        if (!parentId) {
            return;
        }

        setLoadingHierarchyNode((prevState): any => [...prevState, parentId]);
        const { data: childHierarchyData }: any = await getAllGroups({ parent: parentId });
        if (childHierarchyData) {
            setHierarchyMappingData((prevState) => ({
                ...prevState,
                [parentId]: childHierarchyData?.data?.records,
            }));
        }

        setLoadingHierarchyNode((prevState): any => prevState.filter((loadingNode) => loadingNode !== parentId));
    };

    const groupSelectionHandler = (id: string): void => {
        const payloadClone = [...selectedGroups];
        const uncheckedIndex = payloadClone.indexOf(id);
        if (uncheckedIndex !== -1) {
            payloadClone.splice(uncheckedIndex, 1);
        } else if (id) {
            payloadClone.push(id);
        }
        setSelectedGroups(payloadClone);
    };

    const handleChipDelete = (key: any): void => {
        if (tableRef.current) {
            tableRef.current.resetFilters(key === 'groupId' ? 'groupPath' : key, true);
        }
        const paginationPayloadClone = JSON.parse(JSON.stringify(payload));
        delete paginationPayloadClone.filters[key];
        setPayload(paginationPayloadClone);
    };

    const getGroupHeaderOptions = (): JSX.Element => (
        <Hierarchy
            isLoading={!groupsData}
            data={
                defaultGroupAccess
                    ? [
                          {
                              ...currentOrg,
                              name: currentOrgName,
                              groupCount: currentOrgGroupCount,
                              deviceCount: currentOrgDeviceCount,
                              type: 'GROUP',
                              icon: { web: { family: 'material-ui', name: 'CorporateFare' } },
                          },
                      ]
                    : groupsData
            }
            hasChild={(treeItem: any): boolean => treeItem?.groupCount}
            labelKey={'name'}
            hierarchyMappingData={hierarchyMappingData}
            loadingNode={loadingHierarchyNode}
            handleTreeNodeSelection={(parentNode: any): any => {
                groupSelectionHandler(parentNode?.id ?? '');
            }}
            filters={[
                {
                    key: 'type',
                    operator: '!==',
                    value: 'DEVICE',
                    action: 'hidden',
                },
                {
                    key: 'type',
                    operator: '!==',
                    value: 'GATEWAY',
                    action: 'hidden',
                },
            ]}
            autoChildSelection={true}
            loadMore={handleFetchHierarchy}
            loadingSkeleton={<GroupHierarchySkeleton row={1} />}
            selectedNodes={selectedGroups}
            selectionType={'checkbox'} // radio, checkbox, node
        />
    );

    const getActionCell = (data: any): JSX.Element => {
        const getLabel = (): JSX.Element | null => {
            const tagProps = getUpcomingStatusTagProps(data?.status);

            return tagProps ? <ListItemTag {...tagProps} /> : null;
        };

        return (
            <Stack direction={'row'} spacing={2} alignItems={'center'} justifyContent={'end'}>
                {getLabel()}
                {canViewPrediction && (
                    <IconButton
                        data-testid={`upcoming-events-detail-page-btn-${data?.id ?? ''}`}
                        aria-label={`Open details for ${data?.type ?? 'upcoming event'} on ${
                            data?.deviceName ?? 'device'
                        }`}
                        onClick={(): void => {
                            navigate(
                                `/${currentRealmName}/upcomingEventsDetails/${deviceId ?? data?.deviceId}/${data?.id}`
                            );
                        }}
                    >
                        <ChevronRight />
                    </IconButton>
                )}
            </Stack>
        );
    };

    const getStatusStyles = (data: any): any => {
        const borderStyle = getLeftBorderStyle(data?.indicator);
        return borderStyle ?? {};
    };

    const tableRows: Header[] = [
        {
            header: '',
            sx: getStatusStyles,
            cell: (item: any) => getStatusCell(item),
        },
        {
            header: 'Within',
            accessor: 'within',
            isSortable: true,
            isFilterable: true,
            filterSelectionType: 'single',
            filterOptions: [
                { id: 'all', label: <CustomTransComponent translationKey={'SCHEDULING:ALL'} /> },
                { id: 0, label: <CustomTransComponent translationKey={'UPCOMING:NOW'} /> },
                {
                    id: 30,
                    label: <CustomTransComponent translationKey={'UPCOMING:WITHIN_DAYS'} replace={{ count: 30 }} />,
                },
                {
                    id: 60,
                    label: <CustomTransComponent translationKey={'UPCOMING:WITHIN_DAYS'} replace={{ count: 60 }} />,
                },
                {
                    id: 180,
                    label: <CustomTransComponent translationKey={'UPCOMING:WITHIN_DAYS'} replace={{ count: 180 }} />,
                },
                { id: 365, label: <CustomTransComponent translationKey={'UPCOMING:WITHIN_ONE_YEAR'} /> },
            ],
            cell: (data: any) => getDaysCell(data),
        },
        {
            header: t('COMMON:TYPE'),
            accessor: 'type',
            isSortable: true,
            isFilterable: true,
            isDebounce: true,
            cell: (data: any) => getTypeCell(data),
        },
        {
            header: t('COMMON:DEVICE'),
            accessor: 'deviceName',
            isSortable: true,
            isFilterable: true,
            isDebounce: true,
        },
        {
            header: t('COMMON:GROUP'),
            accessor: 'groupId',
            isSortable: true,
            isFilterable: true,
            cell: (data) => getGroupCell(data),
            headerOptions: getGroupHeaderOptions,
        },

        {
            cell: getActionCell,
            header: '',
            // extraOptions: getExtraHeaderOptions,
        },
    ];
    const handleTimelinePageChange = (page: any, size: any): void => {
        setPayload((prev: any) => ({ ...prev, page: page, size: size }));
    };

    const handleFilterChange = (filters: any, sortData?: any): void => {
        const existingFilters = payload.filters;
        const selectedGroupIds = Array.isArray(existingFilters.groupId) ? existingFilters.groupId : [];
        if (JSON.stringify(filters) !== JSON.stringify(payload.filters)) {
            const customKeys: any = {
                ...(deviceId && { deviceIds: [deviceId] }),
                ...(selectedGroupIds.length > 0 && { groupId: selectedGroupIds }),
            };

            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                filters: { ...filters, ...customKeys },
            }));
        }
        if (sortData && Object.keys(sortData)?.length) {
            setPayload((prev: any) => ({
                ...prev,
                page: 0,
                sort: sortData,
            }));
        }
    };

    const [tableFilters] = useFilteredValues({
        allFilters: tableRows,
        triggeredFilters: payload.filters,
    });

    const withinFilter = getWithinFilter(tableFilters?.within);

    const {
        data: upcomingsApiData,
        isLoading,
        isFetching,
    }: any = useGetPredictionFiltersQuery(
        {
            body: {
                ...payload,
                filters: {
                    ...(tableFilters ?? {}),
                    ...(deviceId ? { deviceIds: [deviceId] } : {}),
                    ...withinFilter,
                },
            },
            realm: currentRealmName,
            organizationId: currentOrgId,
        },
        { skip: !currentOrgId || !canReadFilteredPredictions, refetchOnMountOrArgChange: true }
    );

    const resolvedUpcomingsData = React.useMemo(
        () => resolveUpcomingsData(upcomingsApiData, deviceId),
        [upcomingsApiData, deviceId]
    );

    const activeFilters = payload.filters;
    const filterKeys = Object.keys(activeFilters);

    const getChipName = (key: string): string => {
        switch (key) {
            case 'GroupId':
                return t('COMMON:GROUP');
            case 'deviceName':
                return t('COMMON:DEVICE');
            default:
                return toFirstLetterUpperCase(key);
        }
    };

    return (
        <Stack p={3}>
            <Stack direction={'row'} spacing={2} justifyContent={'space-between'} alignItems={'center'}>
                <TextField
                    hiddenLabel
                    id="searchKey"
                    data-testid="upcoming-events-search"
                    value={searchKey}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>): void => {
                        setSearchKey(e.target.value);
                    }}
                    placeholder={t('COMMON:SEARCH_PLACEHOLDER')}
                    size="small"
                    inputProps={{
                        'aria-label': t('COMMON:SEARCH_PLACEHOLDER'),
                        'data-testid': 'upcoming-events-search-input',
                    }}
                    InputProps={{
                        startAdornment: <Search id="search" sx={{ mr: '5px', color: '#727e84' }} />,
                    }}
                />

                {filterKeys.length > 0 && (
                    <Stack
                        direction="row"
                        spacing={1}
                        data-testid="upcoming-events-filter-chips"
                        aria-label="Active filter chips"
                    >
                        {filterKeys.map(
                            (key: string) =>
                                key !== 'deviceIds' && (
                                    <Chip
                                        key={`unique${key}`}
                                        label={getChipName(key)}
                                        data-testid={`upcoming-events-filter-chip-${key}`}
                                        aria-label={`Remove ${getChipName(key)} filter`}
                                        onDelete={(): void => {
                                            handleChipDelete(key);
                                        }}
                                    />
                                )
                        )}
                    </Stack>
                )}
            </Stack>

            <Paper
                className="margin-y-16"
                data-testid="upcoming-events-table-container"
                aria-label="Upcoming events table"
            >
                <CustomTable
                    ref={tableRef}
                    data={resolvedUpcomingsData.records}
                    isPagination
                    handlePageChange={handleTimelinePageChange}
                    isLoading={isLoading || isFetching}
                    keyToTraverse="id"
                    total={resolvedUpcomingsData.total}
                    headers={tableRows}
                    noDataFoundIcon={<Update fontSize="inherit" />}
                    noDataFoundTitle={<CustomTransComponent translationKey={'UPCOMING:NO_UPCOMING_EVENTS'} />}
                    noDataFoundDescription={
                        <CustomTransComponent translationKey={'UPCOMING:NO_UPCOMING_EVENTS_DESCRIPTION'} />
                    }
                    handleFilterChange={handleFilterChange}
                />
            </Paper>
        </Stack>
    );
};
